import { useState } from 'react'
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  Text,
  useBoolean,
  useTheme,
  VStack,
} from '@ritualco/jutsu'
import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import {
  StripeCardElement,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js'
import { FormikInput } from 'components/Formik'
import { useContent } from 'lib/content/react'
import { ReactComponent as AmexLogo } from 'assets/Payment-Amex.svg'
import { ReactComponent as MastercardLogo } from 'assets/Payment-Mastercard.svg'
import { ReactComponent as StripeLogo } from 'assets/Payment-Stripe.svg'
import VisaLogo from 'assets/Payment-Visa.png'
import { HeraModal } from 'components/HeraModal'
import {
  useCreatePaymentMethod,
  useCreateSetupIntent,
} from 'api/endpoints/paymentMethod'
import { useMessage } from 'contexts/MessageContext'
import { useAnalytics } from 'lib/simple-analytics'
import { AnalyticEvents } from 'types/entities'

const activeBorder = { borderColor: 'primary.darker' }
const errorBorder = { borderColor: 'error.normal' }

type PaymentMethodAddForm = {
  name: string
}

const initialValues: PaymentMethodAddForm = {
  name: '',
}

export type PaymentMethodAddModalProps = {
  onSubmit?: () => void
  workspaceId: string
} & Omit<ModalProps, 'children'>

export const PaymentMethodAddModal = ({
  isOpen,
  onClose,
  onSubmit = () => {},
  workspaceId,
  ...modalProps
}: PaymentMethodAddModalProps) => {
  const { formatMessage } = useContent()
  const theme = useTheme()
  const [
    stripeData,
    setStripeData,
  ] = useState<StripeCardNumberElementChangeEvent>()
  const createSetupIntent = useCreateSetupIntent()
  const createPaymentMethod = useCreatePaymentMethod()
  const stripe = useStripe()
  const elements = useElements()
  const [isFocusWithin, setIsFocusWithin] = useBoolean()
  const { addError } = useMessage()
  const { track } = useAnalytics()

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      formatMessage({ id: 'pmportal.billing.error.required' })
    ),
  })

  const handleSubmit = async (values: PaymentMethodAddForm) => {
    // Assert Stripe.js has loaded and there are no errors
    if (!stripe || !elements || !stripeData?.complete) return

    try {
      // Server creates Stripe Customer (if applicable), sets up Stripe setupIntent,
      // and returns clientSecret
      const { setupIntentClientSecret } = await createSetupIntent.mutateAsync({
        workspaceId,
      })

      // Pass clientSecret to Stripe to confirm card and attach card to the Customer
      const res = await stripe.confirmCardSetup(setupIntentClientSecret, {
        payment_method: {
          card: elements.getElement(CardElement) as StripeCardElement,
          billing_details: {
            name: values.name,
          },
        },
      })
      if (res.error) {
        throw res.error
      }
      if (!res.setupIntent.payment_method) {
        throw new Error(
          formatMessage(
            { id: 'pmportal.billing.error.stripePaymentMethod' },
            { id: res.setupIntent.payment_method }
          )
        )
      }

      // Save card on server side
      await createPaymentMethod.mutateAsync({
        workspaceId,
        paymentMethodConfiguration: {
          stripePaymentMethodId: res.setupIntent.payment_method,
        },
      })

      track(AnalyticEvents.actionAddSuccess)
      onSubmit()
      onClose()
    } catch (error) {
      track(AnalyticEvents.actionAddError, { message: error?.message })
      addError({
        description: formatMessage({
          id: 'pmportal.billing.error.invalidCard',
        }),
      })
    }
  }

  return (
    <HeraModal
      size="2xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered={true}
      {...modalProps}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {props => (
          <>
            <ModalHeader>
              {formatMessage({ id: 'pmportal.billing.addCardTitle' })}
              <Text
                fontSize="small"
                fontWeight="normal"
                color="grayscale.secondary"
                mt={2}
              >
                {formatMessage({ id: 'pmportal.billing.addCardDescription' })}
              </Text>
            </ModalHeader>
            <ModalBody>
              <Form id="paymentMethodAdd">
                <VStack spacing={6} mb={6}>
                  <FormikInput
                    name="name"
                    label={formatMessage({
                      id: 'pmportal.billing.addCardNameLabel',
                    })}
                    placeholder={formatMessage({
                      id: 'pmportal.billing.addCardNamePlaceholder',
                    })}
                    isRequired
                  />
                  <FormControl
                    id="cardElement"
                    isInvalid={!!stripeData?.error}
                    isRequired
                  >
                    <FormLabel>
                      {formatMessage({
                        id: 'pmportal.billing.addCardPaymentLabel',
                      })}
                    </FormLabel>
                    <Input
                      as={CardElement}
                      size="lg"
                      py={3}
                      display="flex"
                      alignItems="flex-end"
                      cursor="text"
                      sx={{ '> div': { flex: '1' } }}
                      {...(isFocusWithin && {
                        ...activeBorder,
                        _hover: activeBorder,
                      })}
                      {...(stripeData?.error && {
                        ...errorBorder,
                        _hover: errorBorder,
                      })}
                      onChange={e => {
                        // @ts-ignore - used Input instead of CardElement's onChange type
                        setStripeData(e)
                      }}
                      onBlur={setIsFocusWithin.off}
                      onFocus={setIsFocusWithin.on}
                      options={{
                        style: {
                          base: {
                            fontFamily: theme.fonts.body,
                            fontSmoothing: 'antialiased',
                            fontSize: '16px', // Stripe iframe uses 1rem = 14px so can't use `theme.fontSize`
                            color: theme.colors.grayscale.primary,
                            '::placeholder': {
                              color: theme.colors.grayscale.tertiary,
                            },
                          },
                          invalid: {
                            color: theme.colors.error.normal,
                            iconColor: theme.colors.error.normal,
                          },
                        },
                      }}
                    />
                    <FormErrorMessage>
                      {stripeData?.error?.message}
                    </FormErrorMessage>
                    <HStack justify="flex-end" mt={4}>
                      <AmexLogo />
                      <MastercardLogo />
                      <Image src={VisaLogo} />
                      <StripeLogo />
                    </HStack>
                  </FormControl>
                </VStack>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="transparent" mr={2} onClick={onClose}>
                {formatMessage({ id: 'pmportal.global.actionCancel' })}
              </Button>
              <Button
                type="submit"
                form="paymentMethodAdd"
                isLoading={props.isSubmitting}
                isDisabled={
                  !props.isValid || !props.dirty || !stripeData?.complete
                }
              >
                {formatMessage({
                  id: 'pmportal.billing.addCardSubmit',
                })}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </HeraModal>
  )
}
